// Run-report (A2UI analysis/v1) surfaces for the /a2ui-preview harness. Components are
// real RunReportCard output from dev; each dataModel is trimmed to the paths its
// components bind. `terrain` and `load_recovery` are hand-built from the Figma
// samples (ElevBars · LoadGauge · Notice · Button) until dev emits them.
import type { A2UISurface } from '../components/ChatComponents';
import type { A2UIExample } from './a2uiExamples';
import { CATALOG_ID_ANALYSIS } from './runReport';

type Surface = NonNullable<A2UISurface['createSurface']>;

const wire = (surfaceId: string, components: Surface['components'], dataModel: Surface['dataModel']): A2UISurface => ({
  version: 'v1.0',
  createSurface: { surfaceId, catalogId: CATALOG_ID_ANALYSIS, components, dataModel },
});

export const RUN_REPORT_EXAMPLES: A2UIExample[] = [
  {
    key: 'run_report_entry',
    label: 'RunReport · 첫 화면 (모양 A) — SummaryCard · RumiText · SelectList',
    surface: wire(
      'entry_7f595aea',
      [
        { id: 'root', children: ['summary', 'intro', 'select_list'], component: 'Column' },
        {
          id: 'summary',
          body: '전체적으로 고르게 페이스를 유지하며 달렸어요. 평균 심박은 평소와 비슷한 수준으로 적당하게 뛰었네요.',
          title: { path: '/compare30d/runType_display' },
          eyebrow: '9월 15일 러닝 한 줄 요약',
          component: 'SummaryCard',
        },
        { id: 'intro', text: '어떤 것부터 분석해 드릴까요?', component: 'RumiText' },
        { id: 'select_list', children: ['pace_hr_item', 'splits_item', 'growth_item'], component: 'SelectList' },
        {
          id: 'pace_hr_item',
          icon: '📈',
          label: '러닝 분석 받기',
          action: { event: { name: 'select', context: { id: 'pace_hr', workout_id: 11860 } } },
          component: 'SelectItem',
        },
        {
          id: 'splits_item',
          icon: '⏱',
          label: '구간 기록 분석',
          action: { event: { name: 'select', context: { id: 'splits', workout_id: 11860 } } },
          component: 'SelectItem',
        },
        {
          id: 'growth_item',
          icon: '💬',
          label: '성장 보여줘',
          action: { event: { name: 'select', context: { id: 'growth', workout_id: 11860 } } },
          component: 'SelectItem',
        },
      ],
      { compare30d: { runType_display: '꾸준히 잘 밀고 간 러닝이었어요' } },
    ),
  },
  {
    key: 'run_report_pace_hr',
    label: 'RunReport · pace_hr — Legend · PairBars · StatTiles',
    surface: wire(
      'pace_hr_1de9ce84',
      [
        { id: 'root', children: ['card', 'chips'], component: 'Column' },
        { id: 'card', children: ['badge', 'head', 'legend', 'pair_bars', 'tiles', 'insights'], component: 'Card' },
        { id: 'badge', text: '러닝 분석', tone: 'mint', component: 'Badge' },
        {
          id: 'head',
          body: '이날은 평소보다 페이스를 높여서 달렸어요. 심박수 변화를 확인하며 훈련 강도를 조절해 봐요.',
          title: { path: '/compare30d/runType_display' },
          component: 'HeadGroup',
        },
        { id: 'legend', items: ['페이스', '심박수'], component: 'Legend' },
        { id: 'pair_bars', series: { path: '/recent5' }, component: 'PairBars' },
        {
          id: 'tiles',
          tiles: [
            {
              sub: { path: '/compare30d/paceDelta_display' },
              label: '한 달 평균',
              value: { path: '/compare30d/avgPace_display' },
              subTone: 'good',
            },
            {
              sub: { path: '/compare30d/hrDelta_display' },
              label: '평균 심박',
              value: { path: '/run/hr_display' },
              subTone: 'warn',
            },
            { label: '심장 여유', value: { path: '/compare30d/efficiency_display' }, subTone: 'good' },
          ],
          component: 'StatTiles',
        },
        {
          id: 'insights',
          items: [
            '페이스가 빨라진 만큼 심박수도 함께 올랐어요.',
            '다음에는 조금 더 여유 있게 페이스를 유지하는 연습을 해봐요.',
          ],
          component: 'InsightList',
        },
        { id: 'chips', children: ['chip_growth', 'chip_more'], component: 'ChipRow' },
        {
          id: 'chip_growth',
          text: '📈 성장 비교',
          action: { event: { name: 'select', context: { id: 'growth', workout_id: 11680 } } },
          component: 'Chip',
        },
        {
          id: 'chip_more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11680 } } },
          component: 'Chip',
        },
      ],
      {
        compare30d: {
          avgPace_display: '7\'12"',
          efficiency_display: '+2.0%',
          hrDelta_display: '▲ 6bpm',
          paceDelta_display: '한 달 평균 대비 −86초',
          runType_display: '평소보다 훨씬 빠르게 밀어붙인 러닝이었어요',
        },
        recent5: [
          { hr: 142, label: '8/06', current: false, paceSec: 434, distanceKm: 6.2 },
          { hr: 141, label: '8/07', current: false, paceSec: 428, distanceKm: 5.3 },
          { hr: 146, label: '8/10', current: false, paceSec: 385, distanceKm: 7.0 },
          { hr: 146, label: '8/11', current: false, paceSec: 370, distanceKm: 7.0 },
          { hr: 149, label: '8/11', current: true, paceSec: 347, distanceKm: 7.0 },
        ],
        run: { hr_display: '149bpm' },
      },
    ),
  },
  {
    key: 'run_report_splits',
    label: 'RunReport · splits — HBarRows (전반 파랑 / 후반 주황)',
    surface: wire(
      'splits_cffe4528',
      [
        { id: 'root', children: ['main_card', 'chips'], component: 'Column' },
        { id: 'main_card', children: ['badge', 'head', 'visual', 'tiles', 'insights'], component: 'Card' },
        { id: 'badge', text: '구간 분석', tone: 'orange', component: 'Badge' },
        {
          id: 'head',
          body: '이날은 초반보다 후반에 속도를 더 올리며 마무리했어요. 전체적으로 페이스 변화가 있었지만 끝까지 집중력을 유지했네요.',
          title: { path: '/splits/pattern_display' },
          component: 'HeadGroup',
        },
        { id: 'visual', rows: { path: '/splits/rows' }, component: 'HBarRows' },
        {
          id: 'tiles',
          tiles: [
            {
              sub: { path: '/splits/front_hr_display' },
              label: '전반',
              value: { path: '/splits/front_display' },
              subTone: 'neutral',
            },
            {
              sub: { path: '/splits/backDelta_display' },
              label: '후반',
              value: { path: '/splits/back_display' },
              subTone: 'neutral',
            },
            {
              sub: { path: '/splits/spreadSec_display' },
              label: '고른 정도',
              value: { path: '/splits/spread_display' },
              subTone: 'neutral',
            },
          ],
          component: 'StatTiles',
        },
        {
          id: 'insights',
          items: [
            '구간마다 페이스 변화가 컸지만, 후반부로 갈수록 더 힘차게 달렸어요.',
            '초반 3km 구간에서 페이스가 다소 흔들렸으나 이후 잘 회복했네요.',
          ],
          component: 'InsightList',
        },
        { id: 'chips', children: ['chip1', 'chip2', 'more'], component: 'ChipRow' },
        {
          id: 'chip1',
          text: '📈 한 달 대비 페이스',
          action: { event: { name: 'select', context: { id: 'pace_hr', workout_id: 11680 } } },
          component: 'Chip',
        },
        {
          id: 'chip2',
          text: '📊 최근 5회 흐름',
          action: { event: { name: 'select', context: { id: 'growth', workout_id: 11680 } } },
          component: 'Chip',
        },
        {
          id: 'more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11680 } } },
          component: 'Chip',
        },
      ],
      {
        splits: {
          backDelta_display: '▼ 10초 / 심박 154',
          back_display: '5\'42"',
          front_display: '5\'52"',
          front_hr_display: '평균 심박 141',
          pattern_display: '뒤로 갈수록 빨라진 러닝이었어요',
          rows: [
            { hr: 127, km: 1, half: 'front', paceSec: 322, pace_display: '5\'22"' },
            { hr: 147, km: 2, half: 'front', paceSec: 339, pace_display: '5\'39"' },
            { hr: 148, km: 3, half: 'front', paceSec: 396, pace_display: '6\'36"' },
            { hr: 153, km: 4, half: 'back', paceSec: 349, pace_display: '5\'49"' },
            { hr: 152, km: 5, half: 'back', paceSec: 353, pace_display: '5\'53"' },
            { hr: 152, km: 6, half: 'back', paceSec: 334, pace_display: '5\'34"' },
            { hr: 159, km: 7, half: 'back', paceSec: 334, pace_display: '5\'34"' },
            { hr: 161, km: 8, half: 'partial', paceSec: 368, pace_display: '6\'08"' },
          ],
          spreadSec_display: '편차 74초',
          spread_display: '들쭉날쭉',
        },
      },
    ),
  },
  {
    key: 'run_report_growth',
    label: 'RunReport · growth — DistBars',
    surface: wire(
      'growth_0081a18f',
      [
        { id: 'root', children: ['card', 'chips'], component: 'Column' },
        { id: 'card', children: ['badge', 'head', 'dist_bars', 'stats', 'insight'], component: 'Card' },
        { id: 'badge', text: '성장 비교', tone: 'blue', component: 'Badge' },
        {
          id: 'head',
          body: '지난 러닝들과 비교했을 때 어떤 변화가 있는지 확인해 보세요. 꾸준한 기록이 쌓이면 더 정확한 분석이 가능해요.',
          title: { path: '/growth/title_display' },
          component: 'HeadGroup',
        },
        { id: 'dist_bars', series: { path: '/recent5' }, component: 'DistBars' },
        {
          id: 'stats',
          tiles: [
            {
              sub: { path: '/growth/distDelta_display' },
              label: '거리',
              value: { path: '/run/distance_display' },
              subTone: 'neutral',
            },
            {
              sub: { path: '/growth/paceDelta_display' },
              label: '평균 페이스',
              value: { path: '/run/pace_display' },
              subTone: 'neutral',
            },
            {
              sub: { path: '/growth/hrDelta_display' },
              label: '평균 심박',
              value: { path: '/run/hr_display' },
              subTone: 'neutral',
            },
          ],
          component: 'StatTiles',
        },
        {
          id: 'insight',
          items: ['비슷한 수준으로 꾸준히 달리고 있어요. 지금의 흐름을 유지하며 즐겁게 러닝해 봐요.'],
          component: 'InsightList',
        },
        { id: 'chips', children: ['chip_next', 'more'], component: 'ChipRow' },
        {
          id: 'chip_next',
          text: '🗓 다음 훈련 계획',
          action: { event: { name: 'select', context: { id: 'next_action', workout_id: 11880 } } },
          component: 'Chip',
        },
        {
          id: 'more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11880 } } },
          component: 'Chip',
        },
      ],
      {
        growth: {
          distDelta_display: '4회 평균 대비 ▼ 0.1km',
          hrDelta_display: '4회 평균 대비 ▲ 3bpm',
          paceDelta_display: '4회 평균 대비 ▼ 75초',
          title_display: '요즘 비슷하게 유지 중이에요',
        },
        recent5: [
          { hr: 111, label: '9/11', current: false, paceSec: 275, distanceKm: 1.1 },
          { hr: 87, label: '9/21', current: false, paceSec: 277, distanceKm: 1.4 },
          { hr: 94, label: '9/21', current: false, paceSec: 266, distanceKm: 1.3 },
          { hr: 85, label: '9/21', current: false, paceSec: 265, distanceKm: 1.3 },
          { hr: 97, label: '오늘', current: true, paceSec: 346, distanceKm: 1.2 },
        ],
        run: { distance_display: '1.2km', hr_display: '97bpm', pace_display: '5\'46"' },
      },
    ),
  },
  {
    key: 'run_report_streak',
    label: 'RunReport · streak — CountBars',
    surface: wire(
      'streak_9a54f355',
      [
        { id: 'root', children: ['card', 'chips'], component: 'Column' },
        { id: 'card', children: ['badge', 'head', 'bars', 'tiles', 'insights'], component: 'Card' },
        { id: 'badge', text: '그 주 기록', tone: 'mint', component: 'Badge' },
        {
          id: 'head',
          body: '꾸준히 기록을 쌓아가고 계시네요. 9월 21일에 뛴 러닝이 그 흐름을 잘 이어주고 있어요.',
          title: { path: '/streak/nth_display' },
          component: 'HeadGroup',
        },
        { id: 'bars', series: { path: '/streak/weeks' }, component: 'CountBars' },
        {
          id: 'tiles',
          tiles: [
            { label: '그 주', value: { path: '/streak/thisWeek_display' }, subTone: 'neutral' },
            {
              sub: { path: '/streak/monthDelta_display' },
              label: '그달',
              value: { path: '/streak/month_display' },
              subTone: 'neutral',
            },
            { label: '전달', value: { path: '/streak/lastMonth_display' }, subTone: 'neutral' },
          ],
          component: 'StatTiles',
        },
        { id: 'insights', items: ['기록이 쌓일수록 러닝이 더 편안해질 거예요.'], component: 'InsightList' },
        { id: 'chips', children: ['more'], component: 'ChipRow' },
        {
          id: 'more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11879 } } },
          component: 'Chip',
        },
      ],
      {
        streak: {
          lastMonth_display: '0km',
          monthDelta_display: '▲ 9.0km',
          month_display: '9km',
          nth_display: '그 주 3번째 러닝이었어요',
          thisWeek_display: '3회',
          weeks: [
            { count: 0, label: '8/31', current: false },
            { count: 4, label: '9/7', current: false },
            { count: 0, label: '9/14', current: false },
            { count: 3, label: '그 주', current: true },
          ],
        },
      },
    ),
  },
  {
    key: 'run_report_next_action',
    label: 'RunReport · next_action — PlanList',
    surface: wire(
      'next_action_a3cdaa3d',
      [
        { id: 'root', children: ['card_next_action', 'chip_row'], component: 'Column' },
        {
          id: 'card_next_action',
          children: [
            'badge_next_action',
            'head_group_next_action',
            'plan_list_next_action',
            'insight_list_next_action',
          ],
          component: 'Card',
        },
        { id: 'badge_next_action', text: '다음 액션', tone: 'green', component: 'Badge' },
        {
          id: 'head_group_next_action',
          body: '이번 주 훈련 일정을 확인하고 다음 러닝을 준비해 보세요. 계획대로 움직이면 몸의 부담을 줄이면서 꾸준히 성장할 수 있어요.',
          title: '이번 주 남은 훈련 계획',
          component: 'HeadGroup',
        },
        { id: 'plan_list_next_action', rows: { path: '/plan/rows' }, component: 'PlanList' },
        {
          id: 'insight_list_next_action',
          items: ['다음 훈련까지 충분히 휴식하며 컨디션을 조절해 보세요.'],
          component: 'InsightList',
        },
        { id: 'chip_row', children: ['chip_more'], component: 'ChipRow' },
        {
          id: 'chip_more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11880 } } },
          component: 'Chip',
        },
      ],
      {
        plan: {
          rows: [
            { day: '내일', date: '9/23', title: '회복 조깅 3km', accent: 'primary', detail: '6\'01" 이내' },
            { day: '다음', date: '9/25', title: '이지런 1km', accent: 'primary', detail: '6\'01" 이내' },
            { day: '주말', date: '9/27', title: '롱런 5km', accent: 'primary', detail: '6\'11" 이내' },
          ],
        },
      },
    ),
  },
  {
    key: 'run_report_stride',
    label: 'RunReport · stride — 시각 요소 없는 Card',
    surface: wire(
      'stride_46f81b7d',
      [
        { id: 'root', children: ['stride_card', 'chips'], component: 'Column' },
        { id: 'stride_card', children: ['badge', 'head', 'tiles', 'insight'], component: 'Card' },
        { id: 'badge', text: '보폭', tone: 'blue', component: 'Badge' },
        {
          id: 'head',
          body: '이날 러닝에서는 평소보다 보폭을 넓게 유지하며 뛰었어요. 이전 기록들과 비교해 보아도 확실히 성큼성큼 나아가는 모습이에요.',
          title: { path: '/stride/title_display' },
          component: 'HeadGroup',
        },
        {
          id: 'tiles',
          tiles: [
            {
              sub: { path: '/stride/delta_display' },
              label: '이날 보폭',
              value: { path: '/stride/today_display' },
              subTone: 'good',
            },
            { label: '최근 30일', value: { path: '/stride/avg30_display' }, subTone: 'neutral' },
            {
              sub: { path: '/stride/cadenceDelta_display' },
              label: '걸음 수',
              value: { path: '/stride/cadence_display' },
              subTone: 'good',
            },
          ],
          component: 'StatTiles',
        },
        {
          id: 'insight',
          items: [
            '보폭이 늘어나면서 더 효율적으로 먼 거리를 달릴 수 있게 되었어요.',
            '꾸준히 연습하면 더 편안한 주법을 만들 수 있을 거예요.',
          ],
          component: 'InsightList',
        },
        { id: 'chips', children: ['chip_splits', 'chip_growth', 'chip_more'], component: 'ChipRow' },
        {
          id: 'chip_splits',
          text: '⏱ 구간별로 보기',
          action: { event: { name: 'select', context: { id: 'splits', workout_id: 11680 } } },
          component: 'Chip',
        },
        {
          id: 'chip_growth',
          text: '📈 성장 비교',
          action: { event: { name: 'select', context: { id: 'growth', workout_id: 11680 } } },
          component: 'Chip',
        },
        {
          id: 'chip_more',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11680 } } },
          component: 'Chip',
        },
      ],
      {
        stride: {
          avg30_display: '0.92m',
          cadenceDelta_display: '▲ 12',
          cadence_display: '165',
          delta_display: '▲ 13cm',
          title_display: '보폭이 13cm 늘었어요',
          today_display: '1.05m',
        },
      },
    ),
  },
  {
    key: 'run_report_more',
    label: 'RunReport · more (모양 C) — RumiText · SelectList',
    surface: wire(
      'more_c7b54804',
      [
        { id: 'root', children: ['text1', 'list1', 'text2'], component: 'Column' },
        { id: 'text1', text: '볼 수 있는 분석이에요. 골라 주세요.', component: 'RumiText' },
        { id: 'list1', children: ['item1', 'item2', 'item3', 'item4'], component: 'SelectList' },
        { id: 'text2', text: '다른 게 궁금하면 아래에 바로 물어봐도 돼요.', component: 'RumiText' },
        {
          id: 'item1',
          icon: '📈',
          label: '러닝 분석 받기',
          action: { event: { name: 'select', context: { id: 'pace_hr', workout_id: 11880 } } },
          component: 'SelectItem',
        },
        {
          id: 'item2',
          icon: '💬',
          label: '성장 보여줘 · 봤어요',
          action: { event: { name: 'select', context: { id: 'growth', workout_id: 11880 } } },
          component: 'SelectItem',
        },
        {
          id: 'item3',
          icon: '🗓',
          label: '다음 액션 · 봤어요',
          action: { event: { name: 'select', context: { id: 'next_action', workout_id: 11880 } } },
          component: 'SelectItem',
        },
        {
          id: 'item4',
          icon: '🔥',
          label: '이번 주 기록 · 봤어요',
          action: { event: { name: 'select', context: { id: 'streak', workout_id: 11880 } } },
          component: 'SelectItem',
        },
      ],
      {},
    ),
  },
  {
    key: 'run_report_terrain',
    label: 'RunReport · terrain — ElevBars (Figma 샘플)',
    surface: wire(
      'terrain_sample',
      [
        { id: 'root', component: 'Column', children: ['card', 'chips'] },
        { id: 'card', component: 'Card', children: ['badge', 'head', 'elev', 'tiles'] },
        { id: 'badge', component: 'Badge', text: '오르막 · 내리막', tone: 'orange' },
        {
          id: 'head',
          component: 'HeadGroup',
          title: { path: '/terrain/title_display' },
          body: '3km 구간에서 22m를 올랐어요. 오르막에서도 페이스를 잘 지켰어요.',
        },
        { id: 'elev', component: 'ElevBars', rows: { path: '/terrain/rows' } },
        {
          id: 'tiles',
          component: 'StatTiles',
          tiles: [
            { label: '오르막', value: { path: '/terrain/gain_display' }, subTone: 'neutral' },
            { label: '내리막', value: { path: '/terrain/loss_display' }, subTone: 'neutral' },
          ],
        },
        { id: 'chips', component: 'ChipRow', children: ['more'] },
        {
          id: 'more',
          component: 'Chip',
          text: '＋ 다른 분석 보기',
          action: { event: { name: 'select', context: { id: 'more', workout_id: 11860 } } },
        },
      ],
      {
        terrain: {
          title_display: '오르막이 있는 코스였어요',
          gain_display: '+45m',
          loss_display: '-37m',
          rows: [
            { km: 1, elev: 1 },
            { km: 2, elev: 8 },
            { km: 3, elev: 22 },
            { km: 4, elev: 14 },
            { km: 5, elev: -6 },
            { km: 6, elev: -12 },
            { km: 7, elev: -9 },
            { km: 8, elev: -1 },
          ],
        },
      },
    ),
  },
  {
    key: 'run_report_load_recovery',
    label: 'RunReport · load_recovery — Notice · LoadGauge · Button (Figma 샘플)',
    surface: wire(
      'load_recovery_sample',
      [
        { id: 'root', component: 'Column', children: ['notice', 'card'] },
        {
          id: 'notice',
          component: 'Notice',
          tone: 'warn',
          text: '훈련량·회복 정보를 지금은 불러오지 못했어요. 잠시 후 다시 확인할 수 있어요.',
        },
        { id: 'card', component: 'Card', children: ['badge', 'head', 'gauge', 'insight', 'btn'] },
        { id: 'badge', component: 'Badge', text: '훈련량 · 회복', tone: 'green' },
        {
          id: 'head',
          component: 'HeadGroup',
          title: '훈련량이 딱 좋아요',
          body: '최근 4주 대비 이번 주 훈련량이 알맞은 범위예요.',
        },
        { id: 'gauge', component: 'LoadGauge', level: { path: '/load/level' }, max: 5 },
        {
          id: 'insight',
          component: 'InsightList',
          items: ['지금 흐름을 유지하면 부상 걱정 없이 체력을 올릴 수 있어요.'],
        },
        {
          id: 'btn',
          component: 'Button',
          label: '통증 살펴보기',
          style: 'ghost',
          action: { event: { name: 'open_pain_intake' } },
        },
      ],
      { load: { level: 2.4 } },
    ),
  },
];
